import { type MaintenanceChecklistField } from '@gb-ticket/shared-types';
import { useState } from 'react';

import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { cn } from '@/lib/utils';

type Props = {
  field: MaintenanceChecklistField;
  value: unknown;
  comment?: string | null;
  disabled?: boolean;
  onValueChange: (value: unknown) => void;
  onCommentChange?: (comment: string | null) => void;
  onFileSelected?: (file: File) => void;
};

const CHOICE_ROW_LIMIT = 4;

function ChoiceOptionButton({
  label,
  selected,
  disabled,
  onClick,
}: {
  label: string;
  selected: boolean;
  disabled?: boolean;
  onClick: () => void;
}) {
  return (
    <Button
      type="button"
      variant="outline"
      disabled={disabled}
      className={cn(
        'min-h-11 w-full justify-start text-base font-normal',
        selected && 'border-primary bg-primary/10',
      )}
      onClick={onClick}
    >
      {label}
    </Button>
  );
}

export function ChecklistFieldInput({
  field,
  value,
  comment,
  disabled,
  onValueChange,
  onCommentChange,
  onFileSelected,
}: Props) {
  const [commentOpen, setCommentOpen] = useState(Boolean(comment?.trim()));

  if (field.type === 'info') {
    return (
      <div className="rounded-md border border-dashed bg-muted/40 p-4 text-sm">
        <p className="font-medium">{field.label}</p>
        <p className="text-muted-foreground">{field.content}</p>
      </div>
    );
  }

  const label = (
    <Label className="flex flex-col gap-1 text-base">
      <span>
        {field.label}
        {field.required ? ' *' : ''}
      </span>
      {field.helpText ? (
        <span className="text-sm font-normal text-muted-foreground">{field.helpText}</span>
      ) : null}
    </Label>
  );

  const inputClass = cn('min-h-11 text-base', disabled && 'opacity-70');

  return (
    <div className="space-y-3 py-4 first:pt-0">
      {label}
      {field.type === 'boolean' ? (
        <div className="grid grid-cols-2 gap-2">
          <ChoiceOptionButton
            label="Ja"
            selected={value === true}
            disabled={disabled}
            onClick={() => onValueChange(true)}
          />
          <ChoiceOptionButton
            label="Nein"
            selected={value === false}
            disabled={disabled}
            onClick={() => onValueChange(false)}
          />
        </div>
      ) : null}
      {field.type === 'choice' ? (
        field.options.length <= CHOICE_ROW_LIMIT ? (
          <div className="space-y-2">
            {field.options.map((opt) => (
              <ChoiceOptionButton
                key={opt.value}
                label={opt.label}
                selected={value === opt.value}
                disabled={disabled}
                onClick={() => onValueChange(opt.value)}
              />
            ))}
          </div>
        ) : (
          <Select
            value={typeof value === 'string' ? value : ''}
            onValueChange={(next) => onValueChange(next)}
            disabled={disabled}
          >
            <SelectTrigger className={inputClass}>
              <SelectValue placeholder="Bitte wählen …" />
            </SelectTrigger>
            <SelectContent>
              {field.options.map((opt) => (
                <SelectItem key={opt.value} value={opt.value}>
                  {opt.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        )
      ) : null}
      {field.type === 'short_text' ? (
        <Input
          className={inputClass}
          disabled={disabled}
          value={typeof value === 'string' ? value : ''}
          onChange={(e) => onValueChange(e.target.value)}
        />
      ) : null}
      {field.type === 'long_text' ? (
        <Textarea
          className={inputClass}
          rows={4}
          disabled={disabled}
          value={typeof value === 'string' ? value : ''}
          onChange={(e) => onValueChange(e.target.value)}
        />
      ) : null}
      {field.type === 'integer' || field.type === 'decimal' || field.type === 'measurement' ? (
        <div className="flex items-center gap-2">
          <Input
            type="number"
            className={inputClass}
            disabled={disabled}
            value={typeof value === 'number' ? value : ''}
            onChange={(e) => onValueChange(e.target.value === '' ? null : Number(e.target.value))}
          />
          {field.type === 'measurement' ? (
            <span className="text-sm text-muted-foreground">{field.unit}</span>
          ) : null}
        </div>
      ) : null}
      {field.type === 'date' ? (
        <Input
          type="date"
          className={inputClass}
          disabled={disabled}
          value={typeof value === 'string' ? value : ''}
          onChange={(e) => onValueChange(e.target.value)}
        />
      ) : null}
      {field.type === 'time' ? (
        <Input
          type="time"
          className={inputClass}
          disabled={disabled}
          value={typeof value === 'string' ? value : ''}
          onChange={(e) => onValueChange(e.target.value)}
        />
      ) : null}
      {field.type === 'attachment' ? (
        <Input
          type="file"
          accept="image/*,application/pdf"
          capture="environment"
          className={cn(inputClass, 'w-full')}
          disabled={disabled}
          onChange={(e) => {
            const file = e.target.files?.[0];
            if (file && onFileSelected) {
              onFileSelected(file);
            }
          }}
        />
      ) : null}
      {onCommentChange && field.type !== 'boolean' && field.type !== 'attachment' ? (
        <div className="space-y-2">
          <Button
            type="button"
            variant="ghost"
            size="sm"
            className="h-auto px-0 text-muted-foreground"
            onClick={() => setCommentOpen((open) => !open)}
          >
            Bemerkung{comment?.trim() ? ' (ausgefüllt)' : ''}
          </Button>
          {commentOpen ? (
            <Input
              className="min-h-11 text-base"
              disabled={disabled}
              placeholder="Optionale Bemerkung"
              value={comment ?? ''}
              onChange={(e) => onCommentChange(e.target.value.trim() ? e.target.value : null)}
            />
          ) : null}
        </div>
      ) : null}
    </div>
  );
}
